import cx from "classnames"
import React, { useState, useEffect, useRef } from "react"

import { FlowInputCallback, FlowInputTextType, FlowQuestionId } from "types"
import * as styles from "./styles.module.scss"

export enum InputTextSize {
  Medium = "medium",
  Large = "large",
}

export type InputTextProps = {
  autofocus?: boolean
  className?: string
  disabled?: boolean
  flowKey: FlowQuestionId
  inputType?: FlowInputTextType
  maxLength?: number
  min?: number
  nextCallback?: Function
  onSubmit?: FlowInputCallback
  pattern?: string
  placeholder?: string
  prefixSign?: "$"
  size?: InputTextSize
  step?: number
  suffixSign?: "%"
  value?: string
}

const InputText: React.FC<InputTextProps> = ({
  autofocus,
  className,
  disabled,
  flowKey,
  inputType = FlowInputTextType.Email,
  maxLength,
  min,
  nextCallback,
  onSubmit,
  pattern,
  placeholder,
  prefixSign,
  size = InputTextSize.Large,
  suffixSign,
}) => {
  const [value, setValue] = useState("")
  const input = useRef<HTMLInputElement>(null)

  const handleOnSubmit = (value: string) => {
    if (onSubmit) {
      onSubmit({
        flowKey,
        value,
        display: value,
      })
    }
  }

  const parseFormattedDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (flowKey !== "fullDate") {
      setValue(e.target.value)
      return
    }
    const { value } = e.target
    const INPUT_LENGTH = value.length
    let formattedValue = value

    // [TO] Removes slashes on dates
    if (value.length <= value.length || value === undefined) {
      setValue(value)
      return
    }

    if (INPUT_LENGTH === 2 || INPUT_LENGTH === 5) {
      formattedValue += "/"
      setValue(formattedValue)
      return
    }

    setValue(value)
  }

  const handleKeyPress = (
    { key }: React.KeyboardEvent<HTMLInputElement>,
    value: string
  ) => {
    if (key === "Enter") {
      handleOnSubmit(value)
    }
  }

  useEffect(() => {
    if (!disabled) {
      // TODO: See if we can leverage/spin off our own compat library for this
      if (navigator.userAgent.match(/(iPod|iPhone|iPad)/)) {
        input.current?.focus()
      } else {
        setTimeout(() => {
          input?.current?.focus()
        }, 500)
      }
    }
  })

  const numerical =
    inputType === FlowInputTextType.Date ||
    inputType === FlowInputTextType.FullDate ||
    inputType === FlowInputTextType.Currency ||
    inputType === FlowInputTextType.Percentage

  return (
    <fieldset>
      <div
        className={cx({
          [styles.field]: true,
          [styles[size]]: size,
          [styles.fieldPercentage]: inputType === FlowInputTextType.Percentage,
          [styles.fieldEmail]: inputType === FlowInputTextType.Email,
          [styles.fieldDate]: inputType === FlowInputTextType.Date,
          [styles.fieldFullDate]: inputType === FlowInputTextType.FullDate,
          [styles.fieldCurrency]: inputType === FlowInputTextType.Currency,
          [className]: !!className,
        })}
        data-flow-key={flowKey}
        data-qa-answer-type="input"
        data-qa="answer"
      >
        <div className={styles.wrapper}>
          {prefixSign}
          <input
            className={cx({
              [styles.input]: true,
              [styles.inputPercentage]:
                inputType === FlowInputTextType.Percentage,
              [styles.inputEmail]: inputType === FlowInputTextType.Email,
              [styles.inputDate]: inputType === FlowInputTextType.Date,
              [styles.inputFullDate]: inputType === FlowInputTextType.FullDate,
              [styles.inputCurrency]: inputType === FlowInputTextType.Currency,
            })}
            value={value}
            onChange={(e) => parseFormattedDate(e)}
            data-flow-autofocus={autofocus}
            type={numerical ? "tel" : inputType}
            required
            placeholder={placeholder}
            min={min}
            maxLength={maxLength}
            pattern={pattern}
            data-qa={inputType === "email" ? "email" : "input"}
            ref={input}
            disabled={disabled}
            onKeyPress={(event) => handleKeyPress(event, input.current.value)}
          />
          {suffixSign}
          <a
            className={cx({
              [styles.next]: true,
            })}
            data-flow-next-callback={nextCallback}
            data-qa="submit"
            onClick={() => handleOnSubmit(input.current.value)}
          >
            <i className={styles.arrow}></i>
          </a>
          <div className={styles.border}></div>
        </div>
      </div>
    </fieldset>
  )
}

export default InputText
